// Third party
import React from 'react';
import { withFormik, FormikProps, Form } from 'formik';
import * as Yup from 'yup';

// Components
import { FormInput } from '../forms';
import { PrimaryButton } from '../buttons';
import { SelectInput, SelectOption } from '../inputs';

// Utils
import { formatMessage, FormattedMessage } from '@/utils/i18n';
import { PROVINCE_KEYS, STATE_KEYS } from '@/utils/location';
import Dictionary from '@/types/dictionary';
import User from '@/types/user';
import EditableUserInfos from '@/types/editable-user-infos';
import AppRegion from '@/types/app-region';
import { SETTINGS_FORM_INPUT_STATE } from '@/types/app-config';

enum USER_FIELDS {
  FIRST_NAME = 'fname',
  LAST_NAME = 'lname',
  EMAIL = 'email',
  COMPANY = 'company',
  TITLE = 'title',
  PHONE = 'phone',
  ADDRESS_ONE = 'address1',
  ADDRESS_TWO = 'address2',
  CITY = 'city',
  PROVINCE = 'province',
  COUNTRY = 'country',
  POSTAL_CODE = 'postalCode',
}

const NO_PROVINCE_VALUE = 'NONE';

const INPUT_MASKS = {
  [USER_FIELDS.PHONE.toString()]: { mask: '(999) 999-9999', regex: /\([0-9]{3}\) [0-9]{3}-[0-9]{4}/ },
};

type ParentProps = {
  user: User;
  onSubmit: (info: EditableUserInfos) => Promise<void>;
};

type Props = ParentProps & FormikProps<EditableUserInfos>;

const getCountry = (user: User) => {
  const {
    appConfig: { appRegion },
  } = user;
  return appRegion === AppRegion.US ? 'United States' : 'Canada';
};

class UserOnboarding extends React.Component<Props> {
  setProvince = (value: string) => {
    if (value === NO_PROVINCE_VALUE) {
      this.props.setFieldValue('province', '');
    } else {
      this.props.setFieldValue('province', value);
    }
  };

  getProvinceFieldOptions = () => {
    const {
      user: {
        appConfig: { appRegion },
      },
    } = this.props;

    const EMPTY_OPTION = { key: NO_PROVINCE_VALUE, value: NO_PROVINCE_VALUE, label: ' ' };

    switch (appRegion) {
      case AppRegion.US: {
        return [EMPTY_OPTION].concat(
          STATE_KEYS.map(key => ({
            key,
            value: key,
            label: formatMessage(`States.${key}`),
          })),
        );
      }
      case AppRegion.CAN:
      default: {
        return [EMPTY_OPTION].concat(
          PROVINCE_KEYS.map(key => ({
            key,
            value: key,
            label: formatMessage(`Provinces.${key}`),
          })),
        );
      }
    }
  };

  private nameFields = [
    {
      name: USER_FIELDS.FIRST_NAME,
      required: true,
      disabled: this.props.user.appConfig.settings?.userForm?.fname === SETTINGS_FORM_INPUT_STATE.DISABLED,
    },
    {
      name: USER_FIELDS.LAST_NAME,
      required: true,
      disabled: this.props.user.appConfig.settings?.userForm?.lname === SETTINGS_FORM_INPUT_STATE.DISABLED,
    },
  ];

  private companyFields = [
    {
      name: USER_FIELDS.COMPANY,
      required: true,
      hidden: false,
      disabled: false,
    },
    {
      name: USER_FIELDS.TITLE,
      required: false,
      hidden: false,
      disabled: false,
    },
    {
      name: USER_FIELDS.PHONE,
      required: false,
      disabled: false,
      hidden: false,
      mask: '(999) 999-9999',
      regex: /\([0-9]{3}\) [0-9]{3}-[0-9]{4}/,
    },
    {
      name: USER_FIELDS.EMAIL,
      required: false,
      disabled: true,
      hidden: true,
    },
  ].filter(field => !field.hidden);

  private addressFields = [
    {
      name: USER_FIELDS.ADDRESS_ONE,
      required: false,
      hidden: false,
      disabled: false,
    },
    {
      name: USER_FIELDS.ADDRESS_TWO,
      required: false,
      hidden: false,
      disabled: false,
    },
    {
      name: USER_FIELDS.CITY,
      required: false,
      hidden: false,
      disabled: false,
    },
    {
      name: USER_FIELDS.PROVINCE,
      options: this.getProvinceFieldOptions(),
      required: false,
      hidden: false,
      disabled: false,
    },
    {
      name: USER_FIELDS.COUNTRY,
      value: getCountry(this.props.user),
      required: false,
      hidden: false,
      disabled: true,
    },
    {
      name: USER_FIELDS.POSTAL_CODE,
      required: false,
      hidden: false,
      disabled: false,
    },
  ].filter(field => !field.hidden);

  canSubmit = () => {
    const { values, initialValues, isSubmitting, errors } = this.props;

    const isDirty = values !== initialValues;
    const hasErrors = Object.keys(errors).length > 0;

    return !hasErrors && isDirty && !isSubmitting;
  };

  renderNameFields = () =>
    this.nameFields.map((field, index) => (
      <FormInput key={index} name={field.name} label={formatMessage(`Onboarding.Fields.${field.name}`)} required={field.required} disabled={field.disabled} />
    ));

  renderCompanyFields = () =>
    this.companyFields.map((field, index) => (
      <FormInput
        key={index}
        name={field.name}
        label={formatMessage(`Onboarding.Fields.${field.name}`)}
        required={field.required}
        disabled={field.disabled}
        inputMask={field.mask}
      />
    ));

  renderAddressFields = () =>
    this.addressFields.map((field, index) => {
      // COUNTRY
      if (field.value) {
        return (
          <FormInput
            key={index}
            name={field.name}
            label={formatMessage(`Onboarding.Fields.${field.name}`)}
            value={field.value}
            required={field.required}
            disabled={field.disabled}
          />
        );
      }
      // SELECT
      if (field.options) {
        return (
          <SelectInput
            key={index}
            label={formatMessage(`Onboarding.Fields.${field.name}`)}
            selected={this.props.values.province}
            onValueChange={this.setProvince}
            visibleCount={7}
          >
            {field.options.map(option => (
              <SelectOption key={option.key} value={option.value}>
                {option.label}
              </SelectOption>
            ))}
          </SelectInput>
        );
      }
      // ELSE
      return (
        <FormInput key={index} name={field.name} label={formatMessage(`Onboarding.Fields.${field.name}`)} required={field.required} disabled={field.disabled} />
      );
    });

  render() {
    return (
      <Form className="user-form">
        <div className="fields user-fields">
          <div className="mb-2 d-flex flex-wrap">{this.renderNameFields()}</div>
          <div className="d-flex flex-wrap">{this.renderCompanyFields()}</div>
        </div>
        <h3 className="mb-4">
          <FormattedMessage id="Onboarding.Address" />
        </h3>
        <div className="fields d-flex flex-wrap">{this.renderAddressFields()}</div>
        <PrimaryButton className="submit-button" type="submit" disabled={!this.canSubmit()}>
          <FormattedMessage id="Common.Save" />
        </PrimaryButton>
      </Form>
    );
  }
}

const UserFormSchema = (props: Props) =>
  Yup.lazy(() => {
    const schema: Dictionary<Yup.StringSchema> = {};

    // Set required fields first
    schema[USER_FIELDS.FIRST_NAME] = Yup.string().required();
    schema[USER_FIELDS.LAST_NAME] = Yup.string().required();
    schema[USER_FIELDS.COMPANY] = Yup.string().required();

    // Set input masks after, re-use existing fields
    for (const [key, value] of Object.entries(INPUT_MASKS)) {
      const base = schema[key] || Yup.string();
      schema[key] = base.matches(value.regex);
    }

    return Yup.object().shape(schema);
  });

const formikWrapper = withFormik<Props, EditableUserInfos>({
  validationSchema: UserFormSchema,
  mapPropsToValues: props => ({ ...props.user }),
  handleSubmit: (values, { props, setSubmitting, resetForm }) => {
    const { user } = props;
    const country = getCountry(user);
    const filteredValues = {
      email: values.email,
      fname: values.fname,
      lname: values.lname,
      company: values.company,
      phone: values.phone,
      address1: values.address1,
      address2: values.address2,
      city: values.city,
      postalCode: values.postalCode,
      province: values.province,
      title: values.title,
      country,
    };

    props
      .onSubmit(filteredValues)
      .then(() => {
        resetForm(values);
      })
      .finally(() => {
        setSubmitting(false);
      });
  },
});

export default formikWrapper(UserOnboarding) as any;
